import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IProduct, IVariant } from "redux/reducers/models/product";
import { LocaleCurrency, LocaleCurrencyString } from "helpers/Locale";
import SelectNew from "components/select/SelectNew";

import BookingFeeSearchEmptyState from "./BookingFeeSearchEmptyState";
import {
  flattenBookingFeeProductOptions,
  TBookingFeeProductSelection,
  useBookingFeeProducts,
} from "../hooks/useBookingFeeProducts";

const PRODUCT_SEARCH_INPUT_ID = "booking_fee_product_search";

type TBookingFeeProductSearchProps = {
  product: IProduct | null;
  variant: IVariant | null;
  enabled?: boolean;
  onSelect: (product: IProduct, variant: IVariant) => void;
  onClear: () => void;
  onCreateProduct?: () => void;
};

export default function BookingFeeProductSearch(props: TBookingFeeProductSearchProps) {
  const { t } = useTranslation();
  const { variant, enabled = true, onSelect, onClear, onCreateProduct } = props;

  const { products, isLoading } = useBookingFeeProducts({ enabled });

  const productOptions = useMemo(() => {
    const options = flattenBookingFeeProductOptions(products);
    if (!variant?.id) {
      return options;
    }

    return options.map(option =>
      option.id === variant.id
        ? {
            ...option,
            label: `${option.label} • ${LocaleCurrencyString({ amount: option.extraValue?.variant?.price })}`,
          }
        : option,
    );
  }, [products, variant?.id]);

  return (
    <div className="booking-fees__section booking-fees__product-search">
        <p className="booking-fees__section-label booking-fees__section-label-product">{t("Fee product")}</p>
      <SelectNew<number, TBookingFeeProductSelection>
        id={PRODUCT_SEARCH_INPUT_ID}
        placeholder={t("Search and choose a product...")}
        searchable={300}
        value={variant?.id ?? -1}
        options={productOptions}
        isLoading={isLoading}
        onClear={onClear}
        renderOption={option => (
          <div className="booking-fees__select-option">
            <p className="ui-select_dropdown_text">{option.label}</p>
            <span className="booking-fees__select-option-price">
              <LocaleCurrency amount={option.extraValue?.variant?.price} />
            </span>
          </div>
        )}
        noData={({ search, clearSearch }) => (
          <BookingFeeSearchEmptyState
            title={t("No products found")}
            description={t("Your search '{{search}}' did not match any products.", { search })}
            descriptionSecondary={t("Please try again with a different search or create a new product.")}
            clearLabel={t("Clear search")}
            createLabel={t("New product")}
            onClearSearch={clearSearch}
            onCreate={onCreateProduct}
          />
        )}
        onChange={(_value, extraValue) => {
          if (extraValue?.product && extraValue?.variant) {
            onSelect(extraValue.product, extraValue.variant);
          }
        }}
      />
    </div>
  );
}
